import React, { useState, useEffect, useCallback } from 'react';
import { Book, BookStatus } from '../interfaces.ts';
import { getAllBooks, getStatuses, getBookById } from '../services/apiService.ts';
import SearchInput from '../components/SearchInput.tsx';
import BookList from '../components/BookList.tsx';
import { useNavigate, useLocation } from 'react-router-dom';

const HomePage: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [statuses, setStatuses] = useState<BookStatus[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const fetchBooks = useCallback(async (query?: string) => {
    try {
      const fetchedBooks = await getAllBooks(query);
      setBooks(fetchedBooks);
    } catch (error) {
      console.error('取得書籍時發生錯誤:', error);
    }
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const fetchedStatuses = await getStatuses();
      setStatuses(fetchedStatuses);
    } catch (error) {
      console.error('取得狀態時發生錯誤:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    const { updatedBook, deletedBookId, cancelled, bookId } = location.state || {};

    const handleBookUpdate = async () => {
      if (updatedBook) {
        try {
          const fetchedBook = await getBookById(updatedBook.id);
          setBooks([fetchedBook]);
        } catch (error) {
          console.error('取得更新書籍時發生錯誤:', error);
          setBooks([]); // 獲取失敗則清空列表
        }
      } else if (deletedBookId) {
        setBooks(prevBooks => prevBooks.filter(book => book.id !== deletedBookId));
      } else if (cancelled) {
        if (bookId) {
          // 如果是取消操作且有 bookId，則重新載入該書籍
          try {
            const fetchedBook = await getBookById(bookId);
            setBooks([fetchedBook]);
          } catch (error) {
            console.error('取得取消操作的書籍時發生錯誤:', error);
            setBooks([]); // 獲取失敗則清空列表
          }
        } else {
          // 如果是取消操作且沒有 bookId (新增時取消)，則清空列表
          setBooks([]);
        }
      }

      // 清除 location.state 以避免在後續訪問時重複觸發
      if (location.state) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    handleBookUpdate();
  }, [location.state, navigate]); // 移除 searchTerm 依賴

  useEffect(() => {
    // 只有當 searchTerm 不為空時才執行查詢
    if (searchTerm) {
      fetchBooks(searchTerm);
    } else {
      // 如果 searchTerm 為空，則清空書籍列表，不發送請求
      setBooks([]);
    }
  }, [searchTerm, fetchBooks]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    // 當搜尋詞為空時，清空書籍列表，不發送請求
    if (!term) {
      setBooks([]);
    }
  };

  const handleAddNewBook = () => {
    navigate('/book/new');
  };


  return (
    <div style={{ padding: '20px' }}>
      <h1>書籍查詢系統</h1>
      <button onClick={handleAddNewBook} style={{ marginBottom: '20px', padding: '10px 15px', cursor: 'pointer' }}>
        新增書籍
      </button>
      <div style={{ marginBottom: '20px' }}>
        <SearchInput onSearch={handleSearch} />
      </div>
      <BookList books={books} statuses={statuses} searchTerm={searchTerm} />
    </div>
  );
};

export default HomePage;